import { useState, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { WorkspaceHeader } from './WorkspaceHeader';
import { WorkspaceToolbar } from './WorkspaceToolbar';
import { ExplorerPanel } from './ExplorerPanel';
import { InsightPanel } from './InsightPanel';
import { TimelinePanel } from './TimelinePanel';
import { CopilotPanel } from '../ui';
import type { TimelineEvent, WorkspaceType } from '../../workspaceshell/framework';
import { DEMO_RECOMMENDATIONS } from '../../aix/framework';

type Props = {
  title: string;
  subtitle?: string;
  template?: WorkspaceType;
  organization?: string;
  marketplace?: string;
  brand?: string;
  healthScore?: number;
  status?: string;
  children?: ReactNode;
  timeline?: TimelineEvent[];
  showExplorer?: boolean;
  showAIPanel?: boolean;
  showInsights?: boolean;
  showTimeline?: boolean;
  headerActions?: ReactNode;
  className?: string;
};

export function WorkspaceShell({
  title,
  subtitle,
  template = 'analytics',
  organization = 'AudioPro Inc',
  marketplace = 'Amazon US',
  brand = 'AudioPro',
  healthScore = 87,
  status = 'active',
  children,
  timeline = [],
  showExplorer = true,
  showAIPanel = true,
  showInsights = true,
  showTimeline = true,
  headerActions,
  className,
}: Props) {
  const [activeView, setActiveView] = useState('Table');
  const [explorerOpen, setExplorerOpen] = useState(true);
  const [aiOpen, setAiOpen] = useState(true);

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <WorkspaceHeader
        title={title}
        subtitle={subtitle ?? `${template.replace(/_/g, ' ')} workspace · EDP-00004`}
        status={status}
        organization={organization}
        marketplace={marketplace}
        brand={brand}
        healthScore={healthScore}
        actions={headerActions}
      />

      <WorkspaceToolbar activeView={activeView} onViewChange={setActiveView} />

      <div className="grid gap-4 lg:grid-cols-12">
        {showExplorer && explorerOpen && (
          <div className="lg:col-span-2">
            <ExplorerPanel />
          </div>
        )}

        <div className={cn(
          'space-y-4',
          showExplorer && explorerOpen && showAIPanel && aiOpen ? 'lg:col-span-7' : 'lg:col-span-10',
          !showExplorer && !showAIPanel ? 'lg:col-span-12' : '',
        )}>
          {children ?? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-12 text-center text-sm text-slate-500 dark:border-slate-600 dark:bg-slate-900">
              Main workspace content ({activeView} view)
            </div>
          )}
          {showTimeline && timeline.length > 0 && (
            <TimelinePanel events={timeline} />
          )}
        </div>

        {(showAIPanel || showInsights) && aiOpen && (
          <div className="space-y-4 lg:col-span-3">
            {showInsights && <InsightPanel />}
            {showAIPanel && (
              <CopilotPanel
                recommendations={DEMO_RECOMMENDATIONS}
                contextSummary={`${brand} · ${marketplace}`}
              />
            )}
          </div>
        )}
      </div>

      <div className="flex gap-2 text-xs text-slate-400">
        {showExplorer && (
          <button type="button" onClick={() => setExplorerOpen((o) => !o)} className="hover:text-slate-600">
            {explorerOpen ? 'Hide' : 'Show'} Explorer
          </button>
        )}
        {showAIPanel && (
          <button type="button" onClick={() => setAiOpen((o) => !o)} className="hover:text-slate-600">
            {aiOpen ? 'Hide' : 'Show'} AI Panel
          </button>
        )}
      </div>
    </div>
  );
}
